'use client';
import { useEffect, useMemo, useRef, useState } from 'react';

const MOBILE_MAX = 540;
const TABLET_MAX = 1024;

function calcViewport(orientation, width, height) {
  const shortSide = orientation === 'portrait' ? width : height;
  if (shortSide <= MOBILE_MAX) return 'mobile';
  if (shortSide <= TABLET_MAX) return 'tablet';
  return 'desktop';
}

export const useViewport = () => {
  const hasWindow = typeof window !== 'undefined';
  const hasNavigator = typeof navigator !== 'undefined';

  const [windowWidth, setWindowWidth] = useState(() => (hasWindow ? window.innerWidth : 1200));
  const [windowHeight, setWindowHeight] = useState(() => (hasWindow ? window.innerHeight : 800));
  const [orientation, setOrientation] = useState('landscape');
  const [userAgent, setUserAgent] = useState('none');
  const [isRealMobile, setIsRealMobile] = useState(false);
  const [isTouch, setIsTouch] = useState(false);

  const viewport = useMemo(() => {
    const sizeBased = calcViewport(orientation, windowWidth, windowHeight);
    return isTouch ? sizeBased : 'desktop';
  }, [orientation, windowWidth, windowHeight, isTouch]);

  const rafId = useRef(null);

  useEffect(() => {
    if (!hasWindow) return undefined;

    const handleResize = () => {
      if (rafId.current != null) return;
      rafId.current = window.requestAnimationFrame(() => {
        rafId.current = null;
        setWindowWidth(window.innerWidth);
        setWindowHeight(window.innerHeight);
      });
    };

    handleResize();

    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      window.removeEventListener('resize', handleResize);
      if (rafId.current != null) {
        window.cancelAnimationFrame(rafId.current);
        rafId.current = null;
      }
    };
  }, [hasWindow]);

  useEffect(() => {
    if (!hasWindow) return undefined;

    const mq = window.matchMedia('(orientation: landscape)');
    const update = () => setOrientation(mq.matches ? 'landscape' : 'portrait');

    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, [hasWindow]);

  useEffect(() => {
    if (!hasNavigator) return;

    const ua = navigator.userAgent || '';
    if (ua.includes('iPhone')) setUserAgent('iPhone');
    else if (ua.includes('iPad')) setUserAgent('iPad');
    else if (ua.includes('Android')) setUserAgent('Android');
    else setUserAgent('none');

    const mobile = /iPhone|iPod|Android|Mobile|IEMobile|Opera Mini|Windows Phone|webOS/i.test(ua);
    setIsRealMobile(mobile);

    setIsTouch(navigator.maxTouchPoints > 0 || 'ontouchstart' in window);
  }, [hasNavigator]);

  const isMobile = viewport === 'mobile';
  const isTablet = viewport === 'tablet';
  const isDesktop = viewport === 'desktop';
  const isLandscape = orientation === 'landscape';
  const isPortrait = orientation === 'portrait';

  return {
    viewport,
    isMobile,
    isRealMobile,
    isTablet,
    isDesktop,
    isLandscape,
    isPortrait,
    userAgent,
  };
};
